import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Modal, Button } from 'antd';
import {
    RobotOutlined,
    ThunderboltOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    CodeOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import User from '../../../models/User';
import useResumeVerification from './resume-verification/useResumeVerification';
import {
    ResumeCheckingProgress,
    ResumeDropzone,
    ResumeFailureNotice,
} from './resume-verification/ResumeVerificationParts';

/**
 * Score the talent's roles against their current resume.
 *
 * @returns {Promise<User>} The updated auth user
 */
const requestResumeScores = async () => {
    const response = await TB.request(
        TB.api_endpoint('/api/sessions/update/disciplines/resume')
    ).post();

    const user = User.make(response.success.data.auth);
    await TB.auth().update(user);

    return user;
};

/**
 * The scoring endpoint rejects resumes it can't read.
 */
const isUnreadableResumeError = (error) => !!error?.response?.data?.error?.data?.resume_path;

/* ─── Scoring Loading Animation ─────────────────────────────── */

const STEPS = [
    // { id: 0, icon: FileTextOutlined, label: 'Resume Received', bgColor: 'bg-blue-100', iconColor: 'text-blue-600', status: 'Uploading new resume' },
    { id: 1, icon: RobotOutlined, label: 'AI Analysis', bgColor: 'bg-purple-100', iconColor: 'text-purple-600', status: 'Analyzing resume content' },
    { id: 2, icon: CodeOutlined, label: 'Scoring', bgColor: 'bg-amber-100', iconColor: 'text-amber-600', status: 'Scoring your resume against each role' },
    { id: 3, icon: CheckCircleFilled, label: 'Complete', bgColor: 'bg-green-100', iconColor: 'text-green-600', status: 'Finalizing your resume scores' },
];

const ScoringLoadingAnimation = ({ startStep = 0, skipStep = 0, onFinishAnimation }) => {
    const [currentStep, setCurrentStep] = useState(startStep);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentStep(prev => (prev < STEPS.length - 1 ? prev + 1 : prev));
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (currentStep === STEPS.length - 1 && onFinishAnimation) {
            onFinishAnimation();
        }
    }, [currentStep]);

    const getStepStatus = (stepId) => {
        if (stepId < currentStep) return 'completed';
        if (stepId === currentStep) return 'current';
        return 'pending';
    };

    return (
        <div className="py-6">
            <div className="flex items-center justify-center gap-3 mb-8">
                <div className="h-12 w-12 flex items-center justify-center">
                    <img src="/images/wrex.png" className="w-12 h-12" />
                </div>
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1 mt-0 mx-0">
                        Generating Your Resume Scores
                    </h2>
                    <p className="text-gray-600 m-0 text-sm">
                        <b>Wrex</b> is analyzing your resume and scoring it against each of your selected roles.
                    </p>
                </div>
            </div>

            <div className="space-y-3 max-w-md mx-auto">
                {STEPS.slice(skipStep).map((step, index) => {
                    const status = getStepStatus(index);
                    return (
                        <div key={step.id} className="flex items-center space-x-3">
                            {status === 'completed' && <CheckCircleFilled className="text-lg text-green-500" />}
                            {status === 'current' && <LoadingOutlined className="text-lg text-purple-600" />}
                            {status === 'pending' && <div className="h-5 w-5 rounded-full border-2 border-gray-300" />}
                            <span className={
                                status === 'completed' ? 'text-gray-600' :
                                    status === 'current' ? 'text-purple-600 font-medium' :
                                        'text-gray-400'
                            }>
                                {step.status}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="text-center mt-8 text-xs text-gray-500">
                This usually takes less than a minute. Please don't close this window.
            </div>
        </div>
    );
};

/* ─── Score Modal (existing roles) ──────────────────────────── */

const AIResumeScoreModal = forwardRef(({ disciplines = [], onSuccess, onRequestUpload }, ref) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    const scoreMutation = useMutation({
        mutationFn: requestResumeScores,
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            console.error('AI scoring error:', error);
        }
    });

    useImperativeHandle(ref, () => ({
        open: () => setIsModalVisible(true),
    }));

    const handleConfirm = () => {
        scoreMutation.mutate();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        scoreMutation.reset();
    };

    const disciplinesMissingScores = disciplines.filter(d => !Boolean(d.resume_match_breakdown));

    return (
        <Modal
            title={
                <div className="flex items-center gap-3 text-lg">
                    {/* <RobotOutlined className="text-2xl text-blue-600" /> */}
                    <span>Resume Scoring by Wrex</span>
                </div>
            }
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            maskClosable={!scoreMutation.isLoading}
            closable={!scoreMutation.isLoading}
        >
            {!scoreMutation.isSuccess && !scoreMutation.isError && !scoreMutation.isLoading && (
                <div className="py4">
                    <div className="mb-4 p-4bg-blue-50 borderborder-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue900 mb-2 text-lg">
                            What is Resume Scoring?
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                            Wrex, our AI assistant, analyzes your resume and generates a <strong>Resume Score</strong> for each role you've selected. This score reflects how well your documented experience aligns with the requirements of each role.
                        </p>
                    </div>

                    <div className="mb-4 p-4bg-yellow-50borderborder-yellow-200 rounded-lg">
                        <h4 className="font-semibold text-yellow900 mb-2 text-lg">
                            Why Your Resume Score Matters
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed mb-2">
                            Your Resume Scores play a critical role in how you appear to clients and get matched to projects:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-2">
                            <li>Higher scores improve your visibility in client searches</li>
                            <li>Accurate scores help match you to suitable projects</li>
                            <li>Clients see these scores when reviewing your profile</li>
                            <li>Scores influence which opportunities are presented to you</li>
                        </ul>
                    </div>

                    <div className="mb-6 p-4bg-gray-borderborder-gray-200 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2 text-lg">
                            Before You Continue
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                            Make sure your resume is up-to-date and accurately reflects your experience. Wrex will analyze your current resume to generate a Resume Score for every role you've selected.
                        </p>
                    </div>

                    {disciplinesMissingScores.length > 0 && (
                        <div className="mb-6 p-4 bg-[#EBE1F7] border border-gray-200 rounded-lg">
                            <h4 className="font-semibold text-primary mb-2 text-lg">
                                {/* Your Roles Missing Scores */}
                                Roles Awaiting a Resume Score
                            </h4>
                            <ul className="ml-4 flexflex-wrap">
                                {disciplinesMissingScores.map(d => (
                                    <li key={d.id} className="text-gray-700 text-sm leading-relaxed list-disc">
                                        {d.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <Button
                            onClick={handleCancel}
                            disabled={scoreMutation.isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleConfirm}
                            loading={scoreMutation.isLoading}
                            className="bg-blue-600 hover:bg-blue-700 border-0"
                            icon={<ThunderboltOutlined />}
                        >
                            Generate Resume Scores
                        </Button>
                    </div>
                </div>
            )}

            {scoreMutation.isLoading && (
                <ScoringLoadingAnimation skipStep={0} />
            )}

            {scoreMutation.isSuccess && (
                <div className="pt-10 text-center">
                    <CheckCircleFilled className="text-5xl text-green-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900">All Done!</h3>
                    <p className="text-sm text-gray-500">Your roles have been rescored with the new resume.</p>

                    <div className="flex justify-end gap-3 pt-7">
                        <Button type="primary" onClick={handleCancel}>
                            Close
                        </Button>
                    </div>
                </div>
            )}
            {scoreMutation.isError && (
                isUnreadableResumeError(scoreMutation.error) ? (
                    <div className="pt-10 text-center">
                        <CloseCircleFilled className="text-5xl text-red-500 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900">We can't read your current resume</h3>
                        <p className="text-sm text-gray-500">
                            We need a readable resume to score your roles. Upload a new one and we'll check it before
                            scoring.
                        </p>

                        <div className="flex justify-end gap-3 pt-7">
                            <Button onClick={handleCancel}>Close</Button>
                            {onRequestUpload && (
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        handleCancel();
                                        onRequestUpload();
                                    }}>
                                    Upload a new resume
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="pt-10 text-center">
                        <CloseCircleFilled className="text-5xl text-red-500 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
                        <p className="text-sm text-gray-500">We couldn't finish scoring your roles. Please try again.</p>

                        <div className="flex justify-end gap-3 pt-7">
                            <Button onClick={handleCancel}>Close</Button>
                            <Button type="primary" onClick={handleConfirm}>Try again</Button>
                        </div>
                    </div>
                )
            )}
        </Modal>
    );
});

/* ─── Upload + Rescore Modal ────────────────────────────────── */

/**
 * Upload a new resume, verify it can be read, then rescore every role.
 * If the new resume fails the check, the current resume and its scores
 * stay in place.
 */
const AIResumeUploadAndScoreModal = forwardRef(({ disciplines = [], onSuccess }, ref) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    const scoreMutation = useMutation({
        mutationFn: requestResumeScores,
        onSuccess: (data) => onSuccess?.(data),
        onError: (error) => console.error('AI scoring error:', error),
    });

    const verification = useResumeVerification({
        onVerified: (user) => {
            // The new resume is saved and its old scores were cleared
            onSuccess?.(user);
            scoreMutation.mutate();
        },
    });
    const { phase, file, error } = verification;

    useImperativeHandle(ref, () => ({
        open: () => setIsModalVisible(true),
    }));

    const isBusy = phase === 'checking' || scoreMutation.isLoading;

    const handleCancel = () => {
        if (!isBusy) {
            setIsModalVisible(false);
        }
    };

    const resetState = () => {
        verification.reset();
        scoreMutation.reset();
    };

    const rolesToRescore = disciplines.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600 m-0">
                <strong>{disciplines.length}</strong> {TB.pluralize('role', disciplines.length)} will be rescored:
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
                {disciplines.map(d => (
                    <span key={d.id} className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                        {d.name}
                    </span>
                ))}
            </div>
        </div>
    );

    return (
        <Modal
            title={
                <div className="flex items-center gap-3 text-lg">
                    <span>Upload New Resume & Rescore Roles</span>
                </div>
            }
            open={isModalVisible}
            onCancel={handleCancel}
            afterClose={resetState}
            destroyOnClose
            footer={null}
            width={600}
            closable={!isBusy}
            maskClosable={!isBusy}
            keyboard={!isBusy}
        >
            {phase === 'select' && (
                <div className="py-4">
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-gray-700 text-sm leading-relaxed m-0">
                            Upload an updated resume and we'll check that it can be read, then rescore all of
                            your roles against it. Your current resume and scores stay in place unless the new
                            resume passes the check.
                        </p>
                    </div>

                    <ResumeDropzone
                        file={file}
                        onSelect={verification.selectFile}
                        onRemove={verification.removeFile}
                    />

                    {rolesToRescore}

                    <div className="flex justify-end gap-3 mt-6">
                        <Button onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={verification.verify}
                            disabled={!file}
                            className="bg-blue-600 hover:bg-blue-700 border-0"
                            icon={<ThunderboltOutlined />}
                        >
                            Verify & Rescore Roles
                        </Button>
                    </div>
                </div>
            )}

            {phase === 'checking' && (
                <div className="py-4">
                    <ResumeCheckingProgress fileName={file?.name} />
                </div>
            )}

            {phase === 'failed' && (
                <div className="py-4">
                    <ResumeFailureNotice error={error} fileName={file?.name} hasExistingResume />

                    <div className="mt-5">
                        <ResumeDropzone
                            onSelect={verification.selectFile}
                            prompt="Drop a different file here or"
                        />
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <Button onClick={handleCancel}>
                            Cancel
                        </Button>
                        {error?.canRetry && file && (
                            <Button type="primary" onClick={verification.verify}>
                                Try again
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {phase === 'verified' && scoreMutation.isLoading && (
                <ScoringLoadingAnimation skipStep={0} />
            )}

            {phase === 'verified' && scoreMutation.isSuccess && (
                <div className="pt-10 text-center">
                    <CheckCircleFilled className="text-5xl text-green-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900">All Done!</h3>
                    <p className="text-sm text-gray-500">Your new resume is verified and your roles have been rescored.</p>

                    <div className="flex justify-end gap-3 pt-7">
                        <Button type="primary" onClick={handleCancel}>
                            Close
                        </Button>
                    </div>
                </div>
            )}

            {phase === 'verified' && scoreMutation.isError && (
                <div className="pt-10 text-center">
                    <CloseCircleFilled className="text-5xl text-red-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900">Your resume was saved, but scoring didn't finish</h3>
                    <p className="text-sm text-gray-500">
                        Your new resume passed the check and is on your profile. Try scoring your roles again.
                    </p>

                    <div className="flex justify-end gap-3 pt-7">
                        <Button onClick={handleCancel}>Close</Button>
                        <Button type="primary" onClick={() => scoreMutation.mutate()}>
                            Try scoring again
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
});

export default AIResumeScoreModal;
export { AIResumeUploadAndScoreModal, ScoringLoadingAnimation };